import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { BarChart3, TrendingUp, Eye } from "lucide-react";

export const Charts = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Charts & Analysis Tools</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Learn about the charting and analysis tools available on Nordex</p>
        </div>

        {/* TradingView Chart */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">TradingView Chart</h2>
          
          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Professional Charting</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Nordex integrates{' '}
              <a 
                href="https://www.tradingview.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-semibold"
              >
                TradingView
              </a>
              's advanced charting widget, providing you with professional-grade 
              technical analysis tools directly in the platform. TradingView is the industry-leading 
              charting platform trusted by millions of traders worldwide.
            </p>
          </div>

          <h3 className="text-lg font-semibold mb-4">Features</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li>Multiple timeframes (1m, 5m, 15m, 1h, 4h, 1D, 1W)</li>
            <li>Candlestick, line, bar, and other chart types</li>
            <li>100+ technical indicators (Moving Averages, RSI, MACD, Bollinger Bands, etc.)</li>
            <li>Drawing tools (trend lines, Fibonacci retracements, support/resistance, etc.)</li>
            <li>Save and load chart layouts</li>
            <li>Compare multiple assets</li>
            <li>Volume analysis</li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Accessing the Chart</h3>
          <p className="text-muted-foreground mb-4">
            The Market Chart panel is available in your layout. On desktop, you can maximize it for 
            full-screen analysis by clicking the maximize button in the panel header.
          </p>
        </section>

        {/* Order Book */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Order Book</h2>
          
          <p className="text-muted-foreground mb-6">
            The Order Book panel shows live bid and ask prices with their quantities, updated in real-time 
            via real-time data feeds from professional providers.
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Understanding Order Book</h3>
              <ul className="text-sm text-muted-foreground space-y-2 pl-4 list-disc">
                <li><strong className="text-emerald-500">Green (Bids):</strong> Buy orders - traders willing to buy at these prices</li>
                <li><strong className="text-red-500">Red (Asks):</strong> Sell orders - traders willing to sell at these prices</li>
                <li><strong className="text-foreground">Spread:</strong> Difference between highest bid and lowest ask</li>
                <li><strong className="text-foreground">Depth:</strong> Total quantity at each price level</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Why It Matters</h3>
              <p className="text-sm text-muted-foreground">
                The order book helps you understand market liquidity and potential support/resistance levels. 
                Large orders can indicate strong price levels where many traders are interested.
              </p>
            </div>
          </div>
        </section>

        {/* Market Trades */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Market Trades</h2>
          
          <p className="text-muted-foreground mb-6">
            The Market Trades panel shows a live feed of recent trades with price, size, and timestamp. 
            Trades are color-coded (green for buys, red for sells) to show market sentiment.
          </p>

          <div className="p-4 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-2">Reading Market Trades</h3>
            <ul className="text-sm text-muted-foreground space-y-2 pl-4 list-disc">
              <li>Consecutive green trades (buying pressure) may indicate upward momentum</li>
              <li>Consecutive red trades (selling pressure) may indicate downward momentum</li>
              <li>Large trade sizes can signal institutional activity or significant market moves</li>
              <li>Trade flow can confirm or contradict price action shown in charts</li>
            </ul>
          </div>
        </section>

        {/* Price & Volume Charts */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Price & Volume Charts</h2>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Price Chart</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Simplified price chart showing historical price movement. Useful for quick trend analysis 
                without the full TradingView interface.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <BarChart3 className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Volume Chart</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Displays trading volume over time. High volume periods often coincide with significant 
                price movements and can validate trends.
              </p>
            </div>
          </div>
        </section>

        {/* Customizing Your View */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Customizing Your Analysis Workspace</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Eye className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Panel Management</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Add or remove analysis panels using the "+" button in the header. You can create 
                custom layouts for different trading styles - e.g., a "Chart Focus" layout with 
                maximized TradingView chart, or a "Multi-View" layout with charts, order book, and 
                market trades side by side.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Maximize for Detail</h3>
              <p className="text-sm text-muted-foreground">
                Click the maximize button on the Market Chart panel to expand it to full screen for 
                detailed technical analysis. This is especially useful when drawing trendlines or 
                analyzing complex patterns.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Multiple Layouts</h3>
              <p className="text-sm text-muted-foreground">
                Create different layout configurations for different scenarios. For example, keep one 
                layout for quick scalping with minimal charts, and another for detailed analysis with 
                multiple timeframes.
              </p>
            </div>
          </div>
        </section>

        {/* Analysis Tips */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Analysis Best Practices</h2>
          
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li><strong className="text-foreground">Multiple Timeframes:</strong> Check multiple timeframes before entering a trade. A trend on the 5-minute chart might be counter to the 1-hour trend.</li>
            <li><strong className="text-foreground">Confirm with Volume:</strong> Price movements with high volume are more significant than those with low volume.</li>
            <li><strong className="text-foreground">Order Book Depth:</strong> Check the order book for large buy/sell walls that might act as support or resistance.</li>
            <li><strong className="text-foreground">Market Sentiment:</strong> Use market trades to gauge short-term sentiment and momentum.</li>
            <li><strong className="text-foreground">Save Your Work:</strong> TradingView allows you to save chart layouts with your indicators and drawings for future reference.</li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Account Overview", href: "/account" }}
          next={{ title: "Performance Lab", href: "/performance-lab" }}
        />
      </div>
    </DocsLayout>
  );
};

